import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FiArrowRight, FiCheckCircle, FiBook, FiUsers, FiSettings,
  FiFolder, FiCheckSquare, FiUserPlus, FiGrid, FiLayers
} from 'react-icons/fi';
import PublicPageHeader from '../../components/common/PublicPageHeader';
import PublicPageFooter from '../../components/common/PublicPageFooter';
import DocsSidebar from '../../components/docs/DocsSidebar';

const GettingStarted = () => {
  const steps = [
    {
      number: '01',
      title: 'Create Your Account',
      description: 'Sign up with email or Google. Choose between Personal or Organization workspace based on your needs.',
      icon: FiUsers,
      details: [
        'Go to tasklina.com and click "Get Started"',
        'Choose "Personal" for individual use or "Organization" for team management',
        'Verify your email address',
        'Complete your profile setup'
      ],
      id: 'account'
    },
    {
      number: '02',
      title: 'Set Up Your First Project',
      description: 'Create a project to organize your tasks. Choose a view that works best for you.',
      icon: FiFolder,
      details: [
        'Click "+ New Project" in the sidebar',
        'Enter a project name and description',
        'Choose your default view (Kanban, List, or Calendar)',
        'Add labels and categories for organization'
      ],
      id: 'first-project'
    },
    {
      number: '03',
      title: 'Add Tasks',
      description: 'Create tasks with details, due dates, priorities, and assignees.',
      icon: FiCheckSquare,
      details: [
        'Click "+ Add Task" within your project',
        'Set priority level (Low, Medium, High, Critical)',
        'Add due date and reminders',
        'Attach files and add descriptions',
        'Create subtasks for complex work'
      ],
      id: 'tasks'
    },
    {
      number: '04',
      title: 'Invite Team Members',
      description: 'Collaborate by inviting others to your projects or organization.',
      icon: FiUserPlus,
      details: [
        'Go to project settings or organization settings',
        'Click "Invite Members"',
        'Enter email addresses',
        'Assign appropriate roles and permissions',
        'Team members receive email invitations'
      ],
      id: 'invite'
    }
  ];

  const workspaceComparison = [
    {
      feature: 'Best For',
      personal: 'Individuals, freelancers, small teams',
      organization: 'Companies, departments, large teams'
    },
    {
      feature: 'Team Structure',
      personal: 'Flat - Personal teams',
      organization: 'Hierarchical - Departments & Teams'
    },
    {
      feature: 'User Management',
      personal: 'Simple invitations',
      organization: 'Full RBAC with roles'
    },
    {
      feature: 'Finance Features',
      personal: 'Personal finance tracking',
      organization: 'Budget management, requests, approvals'
    },
    {
      feature: 'Analytics',
      personal: 'Personal productivity stats',
      organization: 'Department & organization analytics'
    },
    {
      feature: 'Billing',
      personal: 'Individual billing',
      organization: 'Centralized billing & subscriptions'
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <PublicPageHeader />
      
      <div className="flex">
        <DocsSidebar currentPath="/docs/getting-started" />
        
        <main className="flex-1 max-w-4xl mx-auto px-6 py-12">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 dark:text-gray-400 mb-8">
            <Link to="/docs" className="hover:text-blue-600 dark:hover:text-blue-400">Docs</Link>
            <span>/</span>
            <span className="text-gray-900 dark:text-white">Getting Started</span>
          </nav>

          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex p-2 bg-blue-100 dark:bg-blue-900/30 dark:bg-blue-900/30 rounded-xl mb-4">
              <FiBook className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Getting Started with Tasklina</h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Learn the basics of Tasklina and get up and running in just a few minutes.
            </p>
          </div>

          {/* Quick Start Steps */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Quick Start Guide</h2>
            <div className="space-y-8">
              {steps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div key={index} id={step.id} className="scroll-mt-24">
                    <div className="flex items-start space-x-6">
                      <div className="flex-shrink-0 w-16 h-16 bg-blue-50 dark:bg-blue-900/30 dark:bg-blue-900/30 rounded-2xl flex items-center justify-center">
                        <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">{step.number}</span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <Icon className="w-5 h-5 text-gray-400 dark:text-gray-500 dark:text-gray-400" />
                          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{step.title}</h3>
                        </div>
                        <p className="text-gray-600 dark:text-gray-300 mb-4">{step.description}</p>
                        <ul className="space-y-2 bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
                          {step.details.map((detail, idx) => (
                            <li key={idx} className="flex items-start space-x-2">
                              <FiCheckCircle className="w-5 h-5 text-green-500 dark:text-green-400 flex-shrink-0 mt-0.5" />
                              <span className="text-gray-700 dark:text-gray-200 dark:text-gray-300">{detail}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Understanding Workspaces */}
          <section className="mb-16" id="workspaces">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Understanding Workspaces</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-8">
              Tasklina offers two types of workspaces to fit different needs. Choose the one that 
              best matches your use case.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 rounded-2xl p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                    <FiUsers className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Personal Workspace</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  Perfect for individuals, freelancers, and small teams who need straightforward 
                  task and project management without complex hierarchies.
                </p>
                <Link 
                  to="/docs/personal" 
                  className="inline-flex items-center text-blue-600 dark:text-blue-400 font-medium hover:underline"
                >
                  Learn more <FiArrowRight className="ml-1" />
                </Link>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/30 rounded-2xl p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center">
                    <FiGrid className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Organization Workspace</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  Designed for companies and enterprises with departments, teams, role-based access, 
                  and comprehensive management features.
                </p>
                <Link 
                  to="/docs/organization" 
                  className="inline-flex items-center text-purple-600 dark:text-purple-400 font-medium hover:underline"
                >
                  Learn more <FiArrowRight className="ml-1" />
                </Link>
              </div>
            </div>

            {/* Comparison Table */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl overflow-hidden">
              <div className="grid grid-cols-3 bg-gray-100 dark:bg-gray-700 dark:bg-gray-700 font-semibold text-gray-900 dark:text-white">
                <div className="p-4">Feature</div>
                <div className="p-4 text-center">Personal</div>
                <div className="p-4 text-center">Organization</div>
              </div>
              {workspaceComparison.map((row, index) => (
                <div key={index} className="grid grid-cols-3 border-t border-gray-200 dark:border-gray-700">
                  <div className="p-4 font-medium text-gray-900 dark:text-white">{row.feature}</div>
                  <div className="p-4 text-gray-600 dark:text-gray-300 text-center text-sm">{row.personal}</div>
                  <div className="p-4 text-gray-600 dark:text-gray-300 text-center text-sm">{row.organization}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Next Steps */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Next Steps</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Link
                to="/docs/personal"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 dark:hover:bg-gray-700 hover:border-blue-200 dark:hover:border-blue-700 border border-transparent transition-all"
              >
                <div className="flex items-center space-x-3">
                  <FiUsers className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  <span className="font-medium text-gray-900 dark:text-white">Personal Workspace Guide</span>
                </div>
                <FiArrowRight className="text-gray-400 dark:text-gray-500 dark:text-gray-400" />
              </Link>
              <Link
                to="/docs/organization"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-purple-50 dark:hover:bg-gray-700 hover:border-purple-200 dark:hover:border-purple-700 border border-transparent transition-all"
              >
                <div className="flex items-center space-x-3">
                  <FiGrid className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  <span className="font-medium text-gray-900 dark:text-white">Organization Workspace Guide</span>
                </div>
                <FiArrowRight className="text-gray-400 dark:text-gray-500 dark:text-gray-400" />
              </Link>
              <Link
                to="/docs/admin"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-red-50 dark:hover:bg-gray-700 hover:border-red-200 dark:hover:border-red-700 border border-transparent transition-all"
              >
                <div className="flex items-center space-x-3">
                  <FiSettings className="w-5 h-5 text-red-600 dark:text-red-400" />
                  <span className="font-medium text-gray-900 dark:text-white">Admin & Permissions</span>
                </div>
                <FiArrowRight className="text-gray-400 dark:text-gray-500 dark:text-gray-400" />
              </Link>
              <Link
                to="/docs/features"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-green-50 dark:hover:bg-gray-700 hover:border-green-200 dark:hover:border-green-700 border border-transparent transition-all"
              >
                <div className="flex items-center space-x-3">
                  <FiLayers className="w-5 h-5 text-green-600 dark:text-green-400" />
                  <span className="font-medium text-gray-900 dark:text-white">Explore Features</span>
                </div>
                <FiArrowRight className="text-gray-400 dark:text-gray-500 dark:text-gray-400" />
              </Link>
            </div>
          </section>

          {/* Article Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Last updated: January 27, 2026
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">Was this helpful?</span>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 dark:bg-gray-800 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
                  Yes
                </button>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 dark:bg-gray-800 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
                  No
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>

      <PublicPageFooter />
    </div>
  );
};

export default GettingStarted;
